import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { leaveRequestService } from '@/services/leaveRequestService'
import { inboxTasksService } from '@/services/inboxTasksService'
import type {
  ApprovalStatusItem,
  LeaveRequestListItem,
  LeaveRequestListResponse,
} from '@/api/types'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'

const APPROVAL_STATUS_LABELS: Record<number, string> = {
  434: 'Pending',
  435: 'Approved',
  436: 'Rejected',
}

export const LeaveRequestList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [rows, setRows] = useState<LeaveRequestListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const columnVisibility: Record<string, boolean> = {
    id: true,
    leaveType: true,
    status: true,
    from: true,
    to: true,
    substitute: true,
    approved: true,
    action: true,
  }

  useEffect(() => {
    const load = async () => {
      try {
        const list = await inboxTasksService.getApprovalStatuses()
        setStatuses(list || [])
      } catch {
        setStatuses([])
      }
    }
    load()
  }, [])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const codeType = statusFilter === 'all' ? undefined : statusFilter
        const res: LeaveRequestListResponse = await leaveRequestService.getAll(
          branchId,
          pageNumber,
          pageSize,
          codeType
        )
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
      } catch (e: any) {
        const msg = (e?.response?.data?.message || e?.message || 'Failed to load leave requests').toString()
        const isNoData = /not found|no data|no records/i.test(msg)
        setRows([])
        setTotalRecords(0)
        if (!isNoData) toast.error(msg)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize, statusFilter])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter(
      (r) =>
        String(r.id).toLowerCase().includes(q) ||
        (r.leaveTypeName ?? '').toLowerCase().includes(q) ||
        (r.statusName ?? '').toLowerCase().includes(q) ||
        (r.fromDate ?? '').toLowerCase().includes(q) ||
        (r.toDate ?? '').toLowerCase().includes(q) ||
        (r.substituteName ?? '').toLowerCase().includes(q) ||
        (r.approvedDate ?? '').toLowerCase().includes(q)
    )
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return String(dateStr).slice(0, 10)
    }
  }

  const statusBadge = (row: LeaveRequestListItem) => {
    const statusCode = Number(row.status ?? 0)
    const mappedText = statusCode ? APPROVAL_STATUS_LABELS[statusCode] : undefined
    const text = mappedText ?? row.statusName ?? (row.status != null ? String(row.status) : '—')
    const isApproved = text.toLowerCase() === 'approved'
    const isPending = text.toLowerCase() === 'pending'
    return (
      <Badge variant={isApproved ? 'default' : isPending ? 'secondary' : 'outline'} className="text-xs">
        {text}
      </Badge>
    )
  }

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm('Delete', 'Are you sure you want to delete this leave request?')
    if (!confirmed) return
    try {
      await leaveRequestService.delete(id)
      await showSuccess('Deleted', 'Leave request deleted successfully.')
      if (branchId) {
        const res = await leaveRequestService.getAll(branchId, pageNumber, pageSize, statusFilter === 'all' ? undefined : statusFilter)
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
      }
    } catch (e: any) {
      await showError('Delete failed', e?.response?.data?.message ?? e?.message ?? 'Failed to delete.')
    }
  }

  // Toolbar buttons (Column Visibility / CSV / Copy / Print) removed as requested.

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Leave Request" description="Leave Request Detail" />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Leave Request"
        description="Leave Request Detail"
        action={{
          label: 'Add Request',
          onClick: () => navigate('/self-services/leave-requests/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="space-y-1">
                <label className="text-sm font-medium">Approval Status *</label>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPageNumber(1) }}>
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {statuses.filter((s) => s.id).map((s) => {
                      const value = String(s.lookupCodeId ?? s.id ?? 0)
                      const label = s.lookupNameEn || s.lookupNameAr || value
                      return (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
            <div className="flex items-center gap-2">
              {/* Intentionally empty: requested to remove list toolbar buttons */}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    {columnVisibility.id && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">ID</TableHead>}
                    {columnVisibility.leaveType && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Leave Type</TableHead>}
                    {columnVisibility.status && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">Status</TableHead>}
                    {columnVisibility.from && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">From</TableHead>}
                    {columnVisibility.to && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">To</TableHead>}
                    {columnVisibility.substitute && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Substitute</TableHead>}
                    {columnVisibility.approved && <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">Approved</TableHead>}
                    {columnVisibility.action && <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-28">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                        No data available in table
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id} className="border-b hover:bg-primary/5">
                        {columnVisibility.id && <TableCell className="px-4 py-2">{row.id}</TableCell>}
                        {columnVisibility.leaveType && <TableCell className="px-4 py-2">{row.leaveTypeName ?? '—'}</TableCell>}
                        {columnVisibility.status && <TableCell className="px-4 py-2 text-center">{statusBadge(row)}</TableCell>}
                        {columnVisibility.from && <TableCell className="px-4 py-2">{formatDate(row.fromDate)}</TableCell>}
                        {columnVisibility.to && <TableCell className="px-4 py-2">{formatDate(row.toDate)}</TableCell>}
                        {columnVisibility.substitute && <TableCell className="px-4 py-2">{row.substituteName ?? '—'}</TableCell>}
                        {columnVisibility.approved && <TableCell className="px-4 py-2">{formatDate(row.approvedDate)}</TableCell>}
                        {columnVisibility.action && (
                          <TableCell className="px-4 py-2 text-center">
                            <div className="inline-flex gap-1">
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-primary hover:bg-primary/10" onClick={() => navigate(`/self-services/leave-requests/${row.id}`)} title="Edit">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => handleDelete(row.id)} title="Delete">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={pageNumber <= 1}>
                    <ChevronLeft className="h-4 w-4" /> Previous
                  </Button>
                  <span className="text-sm">Page {pageNumber} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))} disabled={pageNumber >= totalPages}>
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
